import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { SpeedProfileProperties } from 'src/app/shared/models/speed-profile.model'

@Component({
  selector: 'app-speed-profile-segment-details',
  templateUrl: './speed-profile-segment-details.component.html',
  standalone: false,
})
export class SpeedProfileSegmentDetailsComponent {
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly index = signal(0)
  public readonly selectedFeatureId = signal('')

  constructor(
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length != features.length) {
          this.index.set(0)
        }

        if (features[this.index()].layer.id === `${Layers.SpeedProfile.name}-lines`) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    this.selectedFeatureId.set(this.features()[this.index()].properties?.['id']?.toString() ?? '')
    const properties: SpeedProfileProperties = this.features()[this.index()]
      .properties as SpeedProfileProperties
    this.mapMetaToDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: this.selectedFeatureId(),
    })
  }

  private mapMetaToDetails(properties: SpeedProfileProperties) {
    this.setDetail('Meta', {
      title: 'Speed Profile Segment',
      items: [
        {
          description: 'ID',
          value: this.selectedFeatureId(),
        },
        {
          description: 'way_id',
          value: properties.way_id?.toString() ?? '',
        },
        {
          description: 'average_speed',
          value: `${properties.average_speed?.toFixed(2)} ${properties.speed_unit}`,
        },
        {
          description: 'count',
          value: properties.count?.toString() ?? '',
        },
        {
          description: 'speed_unit',
          value: properties.speed_unit,
        },
      ],
    })
  }

  close() {
    this.clearDetails()
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
